/**
 * What happens when the API says 401.
 *
 * The shared client already tried a single transparent refresh; by the time this
 * handler runs, the session is genuinely gone — idle past five days, signed out
 * elsewhere, or never there (US-00-001.14/.19, US-00-006.10).
 *
 * Three things then have to happen, in this order:
 *
 *   1. remember where the person was, so OTP sign-in returns them to it rather
 *      than to Home (US-00-002.26, US-00-006.21)
 *   2. drop the local half of the session
 *   3. EMPTY THE QUERY CACHE — a cache holding the previous session's queries
 *      would render one user's records to whoever signs in next on that browser
 *      (US-00-006.1)
 *
 * Installed once from the entry point. It is deliberately not a hook: the client
 * calls it from a fetch rejection, where there is no React tree to be inside of.
 *
 * STEP 3 IS DELAYED TO A MACROTASK, DELIBERATELY (TC-US-00-001-9-3). This handler
 * is invoked from inside `rawRequest`, synchronously, before the 401 response has
 * even become the `ApiError` its own caller's `useQuery` is about to reject with
 * — `request()` still has to `throw await ApiError.fromResponse(response)`, which
 * itself awaits `response.json()`. `queryClient.clear()` empties the QueryCache
 * by DESTROYING every Query object, including the very query still in flight;
 * once destroyed, that query's eventual rejection has no Query left to update,
 * so the observer reading it — often the same `useSession()` this 401 came from
 * — is orphaned holding `isPending: true` forever, with nothing to make it try
 * again. A page load on a deep link to a protected route hangs on "Checking your
 * session…" rather than ever reaching the entry screen. `setTimeout(…, 0)` — a
 * macrotask — is what guarantees every microtask this handler's caller still has
 * queued (however many, and this repository does not own that count) has
 * finished before the cache is torn down, so the rejection always lands on a
 * live query first.
 */

import { setUnauthenticatedHandler } from "@anthra/platform/api"
import { logger } from "@anthra/platform/logger"
import { endSession, holdDestination } from "@anthra/platform/session"
import { type QueryClient } from "@tanstack/react-query"

import { useSessionStore } from "@/stores/use-session-store"

/**
 * Wires the 401 handler.
 *
 * `currentPath` is supplied by the router rather than read from `location` here,
 * so the held destination is the app's own route — including its search
 * parameters — and not whatever the address bar happens to show mid-navigation.
 */
export function installUnauthenticatedHandler(
	queryClient: QueryClient,
	currentPath: () => string,
): void {
	setUnauthenticatedHandler(() => {
		// The store flip happens synchronously and first. Several queries can
		// fail with 401 in the same tick; flipping here makes the second and
		// later ones a no-op instead of three concurrent sign-out sequences.
		if (useSessionStore.getState().status === "signed_out") return
		useSessionStore.getState().markSignedOut()

		void endUnauthenticatedSession(queryClient, currentPath())
	})
}

async function endUnauthenticatedSession(queryClient: QueryClient, path: string): Promise<void> {
	try {
		const held = await holdDestination(path)
		if (held) useSessionStore.getState().setDestination(path)
	} catch (error) {
		// Failing to remember a destination costs the person a tap, not a session.
		logger.warn("could not hold the post-sign-in destination", {}, error)
	}

	await endSession()
	// See the module doc comment: yields to a macrotask so the query whose 401
	// caused this handler to run has already turned into an error in the cache,
	// rather than being destroyed by `clear()` while its rejection is still on
	// the way.
	await new Promise<void>(resolve => setTimeout(resolve, 0))
	queryClient.clear()
}
